/**
 * useJobQueue hook — fetches job queue with polling and manages state.
 * See Phase 16.8: auto-refresh every 30 seconds.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchJobQueue, reorderQueue, pinJob, applySuggestedOrder, fetchSuggestedOrder } from '../api/jobs';

const POLL_INTERVAL = 30000; // 30 seconds

export default function useJobQueue() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const data = await fetchJobQueue();
      setJobs(data || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    load(true);
  }, [load]);

  // Polling
  useEffect(() => {
    pollRef.current = setInterval(() => load(false), POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [load]);

  // Optimistic reorder
  const handleReorder = useCallback(async (reorderedJobs) => {
    // Optimistic update
    setJobs(reorderedJobs);

    const items = reorderedJobs.map((job, idx) => ({
      job_id: job.id,
      position: idx + 1,
    }));

    try {
      await reorderQueue(items);
      // Backend runs scheduling recalc synchronously before responding,
      // so a short delay is enough for the DB write to settle.
      setTimeout(() => load(false), 500);
    } catch (err) {
      // Revert on error
      load(false);
    }
  }, [load]);

  // Toggle pin on a single job — optimistic update
  const handlePin = useCallback(async (jobId) => {
    // Optimistic: flip is_queue_pinned locally
    setJobs((prev) =>
      prev.map((j) =>
        j.id === jobId ? { ...j, is_queue_pinned: !j.is_queue_pinned } : j
      )
    );
    try {
      await pinJob(jobId);
    } catch {
      load(false); // revert on error
    }
  }, [load]);

  // Apply suggested order — backend computes, we reload
  const handleApplySuggested = useCallback(async () => {
    try {
      const result = await applySuggestedOrder();
      // Reload to get new queue_position values
      await load(false);
      return result;
    } catch (err) {
      load(false);
      throw err;
    }
  }, [load]);

  // Preview suggested order without applying
  const getSuggestedOrder = useCallback(async () => {
    return fetchSuggestedOrder();
  }, []);

  return {
    jobs,
    loading,
    error,
    refresh: () => load(false),
    handleReorder,
    handlePin,
    handleApplySuggested,
    getSuggestedOrder,
  };
}
